import { CustomEvents } from 'shared/constants/customEvents'
import { LocalStorageKey } from 'shared/constants/localStorage'
import { logger } from 'shared/services/logger'
import {
  verifyRefreshPersistence,
  withAuthStorageReason
} from 'shared/services/logger/utils/authDiagnostics'
import { dispatchCustomEvent } from 'shared/utils/customEvents'
import { lsGetItem, lsRemoveItem } from 'shared/utils/local-storage'

import { refresh } from './requests/refreshToken'

const REFRESH_LOCK_NAME = 'auth-token-refresh'

let refreshInProgress = false

export const isTokenRefreshInProgress = () => refreshInProgress

const adoptToken = (access_token: string, refresh_token: string) => {
  dispatchCustomEvent(CustomEvents.AUTH_TOKEN_UPDATED, {
    access_token,
    refresh_token,
    redirectRoute: false
  })
}

const performRefresh = async (tokenAtRefreshStart: string | null) => {
  // another tab may have refreshed while this one waited for the lock
  // adopt its token instead of refreshing the rotated refresh token again.
  const storedUser = lsGetItem(LocalStorageKey.USER)
  if (
    storedUser?.access_token &&
    storedUser.access_token !== tokenAtRefreshStart
  ) {
    adoptToken(storedUser.access_token, storedUser.refresh_token)
    return Promise.resolve()
  }

  return refresh()
    .then(response => {
      const { access_token, refresh_token } = response.data
      withAuthStorageReason('refresh_success', () =>
        adoptToken(access_token, refresh_token)
      )
      verifyRefreshPersistence(
        logger,
        access_token,
        refresh_token,
        'attemptTokenRefresh'
      )
    })
    .catch(err => {
      // Another tab may have refreshed first — if localStorage has a
      // different (newer) token, adopt it instead of logging out.
      const currentStoredUser = lsGetItem(LocalStorageKey.USER)
      if (
        currentStoredUser?.access_token &&
        currentStoredUser.access_token !== tokenAtRefreshStart
      ) {
        adoptToken(
          currentStoredUser.access_token,
          currentStoredUser.refresh_token
        )
        verifyRefreshPersistence(
          logger,
          currentStoredUser.access_token,
          currentStoredUser.refresh_token,
          'another_tab'
        )
        return
      }
      logger.error('auth.token_refresh_failed', {
        message: err?.message,
        status: err?.response?.status,
        code: err?.response?.data?.error
      })
      withAuthStorageReason('refresh_failed', () =>
        lsRemoveItem(LocalStorageKey.USER)
      )
      dispatchCustomEvent(CustomEvents.AUTH_USER_DATA_UPDATED, {
        user: null
      })
    })
}

export const attemptTokenRefresh = () => {
  if (refreshInProgress) {
    return
  }

  const storedUser = lsGetItem(LocalStorageKey.USER)
  // Sessions on an external-backend token (or without a refresh token, like
  // anonymous ones) cannot be refreshed against the auth service — the
  // issuing backend re-issues via the remote's onLogin instead. Bailing also
  // keeps the failure path below from wiping a still-valid session.
  if (storedUser && (storedUser.isExternalAuth || !storedUser.refresh_token)) {
    return
  }

  refreshInProgress = true
  const tokenAtRefreshStart = storedUser?.access_token ?? null

  // a socket event lands in every open tab at once, so we are placing a lock
  // to make sure refresh is only performed in one tab and adopted in others
  const result = navigator.locks
    ? navigator.locks.request(REFRESH_LOCK_NAME, () =>
        performRefresh(tokenAtRefreshStart)
      )
    : performRefresh(tokenAtRefreshStart)

  result.finally(() => {
    refreshInProgress = false
  })
}
